/**
 * `useOpenCreateContact` — single entry point for opening the canonical
 * Create Contact slideout pre-filled with arbitrary initial values.
 *
 * The same form is opened from multiple surfaces:
 *   - `EntityBadgeByEmail`: unresolved-email chip click → `ahoy_email` only.
 *   - `ParticipantPillList`: same as above (unresolved-email pill click in
 *     meeting Attendees / email-thread Participants).
 *
 * Centralizing the config here means:
 *   1. The form's `createProperties` / `createAssociations` / `entityType`
 *      are sourced from the entity-type registry in ONE place. Any future
 *      change to the contact creation flow (e.g. a new required field, a
 *      different submit path) lands once and propagates to all surfaces.
 *   2. The submit handler invokes `contactService.create` consistently
 *      across all entry points — no chance of one surface drifting to a
 *      different API call.
 *   3. The `CONTACT` entity-type string lives in `core/entityTypes.ts`
 *      and isn't re-declared in each caller.
 *
 * The hook accepts `orgId` because callers know which org they're operating
 * on (most read it off the entity they were rendered for). It returns a
 * stable callback whose only argument is `initialValues` — the form's
 * prefill payload (e.g. `{ ahoy_email: ['ada@example.com'] }`).
 *
 * Opened from inside a record slideout, the create form stacks above it
 * (the create root registers with the overlay order on every open) — the
 * record stays where it was for when the form closes.
 */

import { useCallback } from 'react';

import { useUser } from '@/allauth_auth/hooks';
import { CONTACT } from '@/lib/crm/core/entityTypes';
import { buildGenericCreateConfig } from '@/lib/crm/create/buildGenericCreateConfig';
import { useCreateSlideout } from '@/lib/crm/create/CreateSlideoutContext';
import { getEntityTypeConfig } from '@/lib/crm/record/registry/entityTypeRegistry';
import { contactService } from '@/lib/crm/services';
import type { ContactCreateData } from '@/types/crm';

const CREATE_CONTACT_TITLE = 'Create contact';

export interface UseOpenCreateContactResult {
  /**
   * Open the Create Contact slideout pre-filled with the supplied values.
   *
   * No-op when the current user can't be resolved or the contact registry
   * entry is missing its `createConfig` (defensive — both should always
   * exist in a logged-in org context).
   */
  openCreateContact: (initialValues: Record<string, unknown>) => void;
}

export function useOpenCreateContact(
  orgId: number
): UseOpenCreateContactResult {
  const { open: openCreateSlideout } = useCreateSlideout();
  const user = useUser();

  const openCreateContact = useCallback(
    (initialValues: Record<string, unknown>): void => {
      if (!user?.id) {
        return;
      }
      const contactConfig = getEntityTypeConfig(CONTACT).createConfig;
      if (!contactConfig) {
        return;
      }

      const userId = user.id;
      openCreateSlideout(
        buildGenericCreateConfig({
          orgId,
          entityType: CONTACT,
          title: CREATE_CONTACT_TITLE,
          createProperties: contactConfig.createProperties,
          createAssociations: contactConfig.createAssociations,
          initialValues,
          onSubmit: async (data: Record<string, unknown>) => {
            await contactService.create(
              orgId,
              // The form passes ahoy_* property names; ContactCreateData is
              // a legacy type — contactService.create internally re-casts
              // to Record<string, unknown> and reads ahoy_* keys directly.
              data as unknown as ContactCreateData,
              userId
            );
          },
          submitLabel: CREATE_CONTACT_TITLE,
        })
      );
    },
    [openCreateSlideout, user, orgId]
  );

  return { openCreateContact };
}
